import Alpine from 'alpinejs'
import { roundNumber, formatNumberToEuro } from './utils'

// The datamotive form reports the chosen product by name, so mirror the wording of the calculator buttons.
const LEASE_CHOICE_LABELS: Record<string, string> = {
    financial_lease: 'Zakelijke financiering',
    lease_purchase: 'Private lease',
}

export const initFinanceTool = (): void => {
    Alpine.data(
        'financeTool',
        (price: number, interestRate: number, formId: number, defaultDownPayment: number, defaultTerm: string, defaultFinalTerm: number) => ({
            price,
            interestRate,
            formId,
            isOpen: false,
            downPayment: defaultDownPayment,
            finalTerm: defaultFinalTerm,
            term: defaultTerm,
            activeStep: 0,
            leaseChoice: '',
            isFormSubmitted: false,
            init() {
                document.addEventListener('DOMContentLoaded', () => {
                    const e = new CustomEvent('finance-tool-init', {
                        detail: {
                            financialLease: this.formatNumberToEuro(this.financialLease),
                            leasePurchase: this.formatNumberToEuro(this.leasePurchase),
                        },
                    })
                    window.dispatchEvent(e)
                })
            },
            open() {
                this.isOpen = true
            },
            close(reset = false) {
                this.isOpen = false

                if (reset || this.isFormSubmitted) {
                    this.reset()
                }
            },
            // The nested datamotive form swaps itself for its own success page.
            onFormSuccess() {
                this.isFormSubmitted = true
            },
            get financialLease(): number {
                return this.calculateMonthlyPayment(false)
            },
            get leasePurchase(): number {
                return this.calculateMonthlyPayment(true)
            },
            get monthlyRate(): number {
                return this.leaseChoice === 'lease_purchase' ? this.leasePurchase : this.financialLease
            },
            get vehiclePrice(): number {
                return this.price
            },
            calculateMonthlyPayment(leasePurchase = false): number {
                //Calculate monthly rate
                const monthlyRate = leasePurchase ? roundNumber(100 * (Math.pow(this.interestRate / 100 + 1, 1 / 12) - 1), 8) : this.interestRate / 12

                // Calculate principal (loan amount)
                const principal = this.price - this.downPayment

                // Calculate PMT (Payment) using financial formula
                // PMT = (P - FV/(1+r)^n) * (r * (1 + r)^n) / ((1 + r)^n - 1)
                // where:
                // P = principal
                // FV = future value (balloon payment)
                // r = interest rate per period (monthlyRate/100)
                // n = number of payments (term)
                const rate = monthlyRate / 100
                const payments = parseInt(this.term)
                const futureValue = this.finalTerm

                const monthlyPayment =
                    ((principal - futureValue / Math.pow(1 + rate, payments)) * (rate * Math.pow(1 + rate, payments))) /
                    (Math.pow(1 + rate, payments) - 1)

                return monthlyPayment ? Math.round(monthlyPayment) : 0
            },
            continueToUserData(leaseChoice: string) {
                this.leaseChoice = leaseChoice
                this.nextStep()
                this.fillFormData()
            },
            // Keyed by label: the hidden fields carry generated names, so datamotive matches them on their label.
            financeSummary(): Record<string, string> {
                return {
                    Aanbetaling: this.formatNumberToEuro(this.downPayment),
                    Termijn: String(this.term),
                    Slottermijn: this.formatNumberToEuro(this.finalTerm),
                    Financieringskeuze: LEASE_CHOICE_LABELS[this.leaseChoice] ?? '',
                    'Maandelijks bedrag': this.formatNumberToEuro(this.monthlyRate),
                    'Car URL': window.location.href,
                }
            },
            fillFormData() {
                window.dispatchEvent(
                    new CustomEvent('fill-datamotive-form-data', {
                        detail: { formId: this.formId, dependencyData: this.financeSummary() },
                    })
                )
            },
            reset() {
                this.leaseChoice = ''
                this.downPayment = defaultDownPayment
                this.finalTerm = defaultFinalTerm
                this.term = defaultTerm
                this.activeStep = 0
                this.isFormSubmitted = false
                // The form replaced itself with its success page, so ask it to rebuild before the next visit.
                window.dispatchEvent(new CustomEvent('reset-datamotive-form', { detail: { formId: this.formId } }))
            },
            nextStep() {
                this.activeStep++
            },
            previousStep() {
                this.activeStep--
            },
            formatNumberToEuro(price: number) {
                return formatNumberToEuro(price)
            },
        })
    )
}
